import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { OnboardingSurveyCardId } from '../configs/OnboardingSurveyCardsConfig';
import { ICompanyFormDataType, IPropertyFormDataType } from '../services/forms/FormDataTypes';
import { RootState } from './Store';

// Simple interface for location data
interface ILocationData {
  currentStepId: number | null;
  currentSubstepId: number | null;
  lastLocationUpdated: string | null;
  currentStepName: string | null;
  currentSubstepName: string | null;
  currentPageSlug: string | null;
}


export type IPropertyDraftDataType = Omit<IPropertyFormDataType, "file">;

export type ISurveyDraftAnswers = Record<number, any>;

export type IUtilityManualDraftDataType = {
  electric: string;
  gas: string;
  uapiManualId?: number;
};

// Simplified state interface without progress tracking
interface IOnboardingState {
  // Current Location (where user currently is)
  currentLocation: {
    stepId: number | null;
    substepId: number | null;
    lastUpdated: string | null;
    stepName: string | null;
    substepName: string | null;
    pageSlug: string | null;
  };

  companyDraft: ICompanyFormDataType | null;
  propertyDraft: IPropertyDraftDataType | null;
  surveyDrafts: Partial<Record<OnboardingSurveyCardId, ISurveyDraftAnswers>>;
  utilityManualDraft: IUtilityManualDraftDataType | null;

  /** Hub multi-select (survives hub ↔ survey navigation) */
  selectedSurveyIds: OnboardingSurveyCardId[];
  /** Ordered remaining surveys to run after Start Selected */
  surveyQueue: OnboardingSurveyCardId[];

  // Error States
  error: string | null;
}

const initialState: IOnboardingState = {
  currentLocation: {
    stepId: null,
    substepId: null,
    lastUpdated: null,
    stepName: null,
    substepName: null,
    pageSlug: null,
  },
  companyDraft: null,
  propertyDraft: null,
  surveyDrafts: {},
  utilityManualDraft: null,
  selectedSurveyIds: [],
  surveyQueue: [],
  error: null,
};

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    // Location Management
    restoreLocationFromStorage: (state) => {
      try {
        const savedLocation = localStorage.getItem("onboarding-location");
        if (savedLocation) {
          const { stepId, substepId, lastUpdated } = JSON.parse(savedLocation);
          state.currentLocation = {
            stepId: stepId || null,
            substepId: substepId || null,
            lastUpdated: lastUpdated || null,
            stepName: null,
            substepName: null,
            pageSlug: null,
          };
        }
      } catch (error) {
        // Silent fail - location will remain at default state
        console.warn("Failed to restore location from localStorage:", error);
      }
    },

    setCurrentLocation: (state, action: PayloadAction<ILocationData>) => {
      state.currentLocation = {
        stepId: action.payload.currentStepId,
        substepId: action.payload.currentSubstepId,
        lastUpdated: action.payload.lastLocationUpdated,
        stepName: action.payload.currentStepName,
        substepName: action.payload.currentSubstepName,
        pageSlug: action.payload.currentPageSlug,
      };
      state.error = null;
    },

    // Error Management
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    setCompanyDraft: (state, action: PayloadAction<ICompanyFormDataType>) => {
      state.companyDraft = action.payload;
    },

    clearCompanyDraft: (state) => {
      state.companyDraft = null;
    },

    setPropertyDraft: (state, action: PayloadAction<IPropertyDraftDataType>) => {
      state.propertyDraft = action.payload;
    },

    clearPropertyDraft: (state) => {
      state.propertyDraft = null;
    },

    setSurveyDraft: (
      state,
      action: PayloadAction<{
        cardId: OnboardingSurveyCardId;
        answers: ISurveyDraftAnswers;
      }>
    ) => {
      state.surveyDrafts[action.payload.cardId] = action.payload.answers;
    },

    clearSurveyDraft: (state, action: PayloadAction<OnboardingSurveyCardId>) => {
      delete state.surveyDrafts[action.payload];
    },

    setUtilityManualDraft: (
      state,
      action: PayloadAction<IUtilityManualDraftDataType>
    ) => {
      state.utilityManualDraft = action.payload;
    },

    clearUtilityManualDraft: (state) => {
      state.utilityManualDraft = null;
    },

    setSelectedSurveyIds: (
      state,
      action: PayloadAction<OnboardingSurveyCardId[]>
    ) => {
      state.selectedSurveyIds = action.payload;
    },

    setSurveyQueue: (
      state,
      action: PayloadAction<OnboardingSurveyCardId[]>
    ) => {
      state.surveyQueue = action.payload;
    },

    dequeueSurvey: (
      state,
      action: PayloadAction<OnboardingSurveyCardId>
    ) => {
      state.surveyQueue = state.surveyQueue.filter((id) => id !== action.payload);
      state.selectedSurveyIds = state.selectedSurveyIds.filter(
        (id) => id !== action.payload
      );
    },

    clearSurveyHubSelection: (state) => {
      state.selectedSurveyIds = [];
      state.surveyQueue = [];
    },

    // Session Management
    resetOnboardingState: (state) => {
      return { ...initialState };
    },

    // Removed static schema actions - now using direct OnboardStepsConfig references
  },
});

// Actions
export const {
  restoreLocationFromStorage,
  setCurrentLocation,
  setError,
  clearError,
  setCompanyDraft,
  clearCompanyDraft,
  setPropertyDraft,
  clearPropertyDraft,
  setSurveyDraft,
  clearSurveyDraft,
  setUtilityManualDraft,
  clearUtilityManualDraft,
  setSelectedSurveyIds,
  setSurveyQueue,
  dequeueSurvey,
  clearSurveyHubSelection,
  resetOnboardingState,
} = onboardingSlice.actions;

// Selectors
export const onboardingSelector = (state: RootState) => state.onboarding;
export const currentLocationSelector = (state: RootState) =>
  state.onboarding.currentLocation;
export const companyDraftSelector = (state: RootState) =>
  state.onboarding.companyDraft;
export const propertyDraftSelector = (state: RootState) =>
  state.onboarding.propertyDraft;
export const surveyDraftsSelector = (state: RootState) =>
  state.onboarding.surveyDrafts;
export const surveyDraftSelector = (
  state: RootState,
  cardId: OnboardingSurveyCardId
): ISurveyDraftAnswers | null => state.onboarding.surveyDrafts[cardId] ?? null;
export const utilityManualDraftSelector = (state: RootState) =>
  state.onboarding.utilityManualDraft;
export const selectedSurveyIdsSelector = (state: RootState) =>
  state.onboarding.selectedSurveyIds;
export const surveyQueueSelector = (state: RootState) =>
  state.onboarding.surveyQueue;

// Removed static schema selectors - now using direct OnboardStepsConfig references

export default onboardingSlice.reducer;
